import { styled, useTheme } from '@mui/material/styles';
import { createLoadingShimmerStyles } from '@bloomays-lib/ui.shared';

export type PrimeGoalProgressKpiAccent = 'green' | 'purple';

export type PrimeGoalProgressKpiProps = {
  title: string;
  valuePrimary: string;
  valueSecondary: string;
  percent: number;
  progressBarPercent: number;
  accent: PrimeGoalProgressKpiAccent;
  loading?: boolean;
  showProgress?: boolean;
  exceededBadge?: string;
};

const clampPercent = (value: number): number => Math.min(100, Math.max(0, Math.round(value)));

const PrimeGoalProgressKpi = ({
  title,
  valuePrimary,
  valueSecondary,
  percent,
  progressBarPercent,
  accent,
  loading = false,
  showProgress = true,
  exceededBadge,
}: PrimeGoalProgressKpiProps) => {
  const theme = useTheme();
  const percentColor = accent === 'green' ? theme.palette.green[100] : theme.palette.purple[100];
  const progressGradient =
    accent === 'green'
      ? `linear-gradient(90deg, ${theme.palette.green[100]}, ${theme.palette.green[60]})`
      : `linear-gradient(90deg, ${theme.palette.purple[100]}, ${theme.palette.purple[60]})`;

  return (
    <KpiContainer $loading={loading}>
      <TopRow>
        <TitleGroup>
          <KpiTitle>{title}</KpiTitle>
          {exceededBadge ? <ExceededBadge>{exceededBadge}</ExceededBadge> : null}
        </TitleGroup>
        {showProgress ? <PercentLabel $color={percentColor}>{loading ? '...' : `${percent} %`}</PercentLabel> : null}
      </TopRow>
      <ValueRow>
        <ValuePrimary>{valuePrimary}</ValuePrimary>
        <ValueSecondary>{valueSecondary}</ValueSecondary>
      </ValueRow>
      {showProgress ? (
        <ProgressTrack>
          <ProgressFill $gradient={progressGradient} $percent={clampPercent(progressBarPercent)} />
        </ProgressTrack>
      ) : null}
    </KpiContainer>
  );
};

export default PrimeGoalProgressKpi;

const KpiContainer = styled('div', {
  shouldForwardProp: (prop) => prop !== '$loading',
})<{ $loading: boolean }>(({ theme, $loading }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(1.25),
  flex: '1 1 0',
  minWidth: 0,
  padding: '18px 22px',
  ...createLoadingShimmerStyles({ active: $loading, contentOpacity: 0.6, speedMs: 1200, lightOpacity: 0.6 }),
}));

const TopRow = styled('div')(() => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 10,
}));

const TitleGroup = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  flexWrap: 'wrap',
  minWidth: 0,
}));

const ExceededBadge = styled('span')(({ theme }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  padding: '2px 8px',
  borderRadius: 999,
  backgroundColor: theme.palette.purple[20],
  color: theme.palette.purple[100],
  fontFamily: '"Poppins SemiBold", sans-serif',
  fontSize: '10.5px',
  fontWeight: 600,
  letterSpacing: '0.03em',
  textTransform: 'uppercase',
  whiteSpace: 'nowrap',
}));

const KpiTitle = styled('span')(({ theme }) => ({
  fontFamily: '"Poppins SemiBold", sans-serif',
  fontSize: '10.5px',
  fontWeight: 600,
  letterSpacing: '0.05em',
  textTransform: 'uppercase',
  color: theme.palette.neutral[50],
  lineHeight: 1.2,
}));

const PercentLabel = styled('span')<{ $color: string }>(({ $color }) => ({
  fontFamily: '"Poppins SemiBold", sans-serif',
  fontSize: '12.5px',
  fontWeight: 600,
  lineHeight: 1.2,
  color: $color,
  whiteSpace: 'nowrap',
}));

const ValueRow = styled('div')(() => ({
  display: 'flex',
  alignItems: 'baseline',
  flexWrap: 'wrap',
  gap: 6,
}));

const ValuePrimary = styled('span')(({ theme }) => ({
  fontFamily: '"Poppins Bold", sans-serif',
  fontSize: '20px',
  fontWeight: 700,
  lineHeight: 1.1,
  color: theme.palette.neutral[90],
  whiteSpace: 'nowrap',
}));

const ValueSecondary = styled('span')(({ theme }) => ({
  fontFamily: '"Poppins Regular", sans-serif',
  fontSize: '12.5px',
  fontWeight: 400,
  color: theme.palette.neutral[50],
  whiteSpace: 'nowrap',
}));

const ProgressTrack = styled('div')(({ theme }) => ({
  width: '100%',
  height: 9,
  borderRadius: 999,
  backgroundColor: theme.palette.neutral[20],
  overflow: 'hidden',
}));

const ProgressFill = styled('div')<{ $gradient: string; $percent: number }>(({ $gradient, $percent }) => ({
  height: '100%',
  width: `${$percent}%`,
  borderRadius: 999,
  background: $gradient,
  transition: 'width 0.2s ease',
}));
